import { LightIcon } from "@xola/icons";
import { Alert } from "@xola/ui-kit";
import { sortBy } from "lodash";
import React, { useMemo } from "react";
import { PartnerObject, XolaPartner, XolaPartnerStatus } from "types";
import { PartnerTable } from "./PartnerTable";
import { TableSkeleton } from "@/components/Common/Skeleton/TableSkeleton";
import { useSeller } from "@/hooks/useAccount";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";

export const enum PartnerFilter {
    ALL = 0,
    CONNECTED = 1,
    PENDING = 2,
    DISCONNECTED = 3,
}
interface PartnerListProps {
    search: string;
    currentFilter: PartnerFilter;
    handlePartnerSelection: (selectedPartners: string[]) => void;
    onPartnerClick: (partner: XolaPartner) => void;
}

export const PartnerList = ({ search, currentFilter, onPartnerClick, handlePartnerSelection }: PartnerListProps) => {
    const seller = useSeller();
    const { data: partners, isLoading } = useAutoFetchCollection<XolaPartner>(["partners"], {
        seller: seller.id,
        expand: "purchaseRule,partnerSeller",
        object: PartnerObject.XOLA_PARTNER,
    });

    const filteredPartners = useMemo(() => {
        if (!partners) return [];

        // Sort once and apply the current filter
        const sortedAndFiltered = sortBy(partners, "status").filter((partner) => {
            if (currentFilter === PartnerFilter.ALL) return true;
            if (currentFilter === PartnerFilter.CONNECTED) return partner.status === XolaPartnerStatus.ACCEPTED;
            if (currentFilter === PartnerFilter.PENDING) return partner.status === XolaPartnerStatus.PENDING;
            if (currentFilter === PartnerFilter.DISCONNECTED) return partner.status === XolaPartnerStatus.DISCONNECTED;
            return true;
        });

        // Apply the search filter
        if (!search.trim()) return sortedAndFiltered;
        const lowerCaseSearchTerm = search.trim().toLowerCase();
        return sortedAndFiltered.filter((partner) =>
            partner.partnerSeller.name?.toLowerCase().includes(lowerCaseSearchTerm),
        );
    }, [partners, currentFilter, search]);

    if (isLoading) {
        return <TableSkeleton rows={7} columns={5} />;
    }

    if (partners.length === 0) {
        return <NoOperatorAlert />;
    }

    if (filteredPartners.length === 0) {
        return (
            <span className="flex items-center justify-center pt-25" data-testid="agent-list-no-agents-label">
                No Operator found.
            </span>
        );
    }

    return filteredPartners.length > 0 ? (
        <PartnerTable
            partners={filteredPartners}
            onSelectPartners={handlePartnerSelection}
            onPartnerClick={onPartnerClick}
        />
    ) : (
        <span className="flex items-center justify-center pt-25" data-testid="agent-list-no-agents-label">
            No Operators found. Click the button above to create a new operator
        </span>
    );
};

const NoOperatorAlert = () => {
    return (
        <Alert color="primary">
            <div className="flex items-center p-2">
                <div className="ml-2 flex-1" data-testid="">
                    <div className="mb-3 flex space-x-2">
                        <LightIcon size="medium" />
                        <span className="text-md font-bold">No Operators</span>
                    </div>
                    <span className="text-base">
                        Click on the &apos;Add Operator&apos; button above to find and connect with other operators
                    </span>
                </div>
            </div>
        </Alert>
    );
};
